import React, { useMemo, useEffect, useState } from 'react';
import { Text, Paper, Loader, Center } from '@mantine/core';
import PdfVirtualViewer from './PdfVirtualViewer';
import type { Uploaded } from '@utils/extractionUtils';
import { getFileKind, parsePageNumbers } from '@utils/extractionUtils';

interface FilePreviewProps {
    uploadedFile: Uploaded;
    pageNumbers: string;
    numPages: number | null;
    onDocumentLoadSuccess: ({ numPages }: { numPages: number }) => void;
}

const FilePreview: React.FC<FilePreviewProps> = ({
    uploadedFile,
    pageNumbers,
    numPages,
    onDocumentLoadSuccess
}) => {
    const fileKind = useMemo(() => getFileKind(uploadedFile), [uploadedFile]);

    const [imageSrc, setImageSrc] = useState<string | null>(null);
    const [imgError, setImgError] = useState<string | null>(null);

    useEffect(() => {
        setImgError(null);
        setImageSrc(null);

        if (!uploadedFile) return;

        if (typeof uploadedFile === 'string') {
            setImageSrc(uploadedFile);
            return;
        }

        let url: string | null = null;
        url = URL.createObjectURL(uploadedFile);
        setImageSrc(url);

        return () => {
            if (url) URL.revokeObjectURL(url);
        };
    }, [uploadedFile]);

    useEffect(() => {
        if (fileKind === 'image') {
            onDocumentLoadSuccess({ numPages: 1 });
        }
    }, [fileKind, onDocumentLoadSuccess]);

    const parsedCommittedPages = useMemo(() => {
        const parsed = parsePageNumbers(pageNumbers);
        if (!numPages) return parsed;
        const filtered = parsed.filter((p) => p >= 1 && p <= numPages);
        return Array.from(new Set(filtered)).sort((a, b) => a - b);
    }, [pageNumbers, numPages]);

    if (!uploadedFile) {
        return (
            <Paper withBorder className="pdf-viewer-container">
                <Center h="100%">
                    <Text size="sm" c="dimmed">
                        No file uploaded.
                    </Text>
                </Center>
            </Paper>
        );
    }

    if (fileKind === 'pdf') {
        return (
            <Paper withBorder className="pdf-viewer-container">
                <PdfVirtualViewer
                    file={uploadedFile}
                    pages={
                        parsedCommittedPages.length > 0
                            ? parsedCommittedPages
                            : Array.from({ length: numPages || 0 }, (_, i) => i + 1)
                    }
                    onLoadSuccess={onDocumentLoadSuccess}
                />
            </Paper>
        );
    }

    if (fileKind === 'image') {
        return (
            <Paper withBorder className="pdf-viewer-container">
                <div className="image-view-root">
                    {!imageSrc && !imgError && (
                        <Center h="100%">
                            <Loader />
                        </Center>
                    )}

                    {imgError && (
                        <Center h="100%" p="md" ta="center">
                            <Text size="sm" c="dimmed">
                                Unable to preview image. {imgError}
                            </Text>
                        </Center>
                    )}

                    {imageSrc && !imgError && (
                        <img
                            key={imageSrc}
                            src={imageSrc}
                            className="uploaded-image"
                            alt="Uploaded"
                            onError={() => setImgError('File not found or unsupported format')}
                        />
                    )}
                </div>
            </Paper>
        );
    }

    return (
        <Paper withBorder className="pdf-viewer-container">
            <Center h="100%">
                <Text size="sm" c="dimmed">
                    Unsupported file type.
                </Text>
            </Center>
        </Paper>
    );
};

export default FilePreview;
